import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import Spinner from '../../global/Spinner';
import AlbumFolderPurchaseSelectAccountIdentitiesContainer from '../../../containers/purchase/album-folder-purchase/AlbumFolderPurchaseSelectAccountIdentitiesContainer';
import Button from '../../global/Button';
import {
  isAlbumFolderTagSelectionRequiredForCheckout,
  canSelectActivitities,
  isPrepaidOnlyAlbumFolder,
} from '../../../util/album-folder';
import WaldoSpeechBubble from '../../global/WaldoSpeechBubble';
import OnboardingLayoutFooter from '../../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import CustomControl from '../../global/CustomControl';
import {
  ALBUM_IDENTITY_ACCESS,
  IDENTITY_RELATIONSHIP,
} from '../../../constants/identity';
import ContextualString from '../../contextual/ContextualString';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  loading: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  selectedIdentities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  identities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  resetTagSelection: PropTypes.func,
  allowsPrepaidItems: PropTypes.bool,
};

const AlbumFolderPurchaseSelectIdentities = ({
  albumFolder,
  loading,
  history,
  selectedIdentities,
  identities,
  tags,
  resetTagSelection,
  allowsPrepaidItems,
}) => {
  const [identityFilter, setIdentityFilter] = useState(null);
  const [identityFilterStepCompleted, setIdentityFilterStepCompleted] =
    useState(false);

  const onGoToCheckout = event => {
    event.preventDefault();

    if (
      isAlbumFolderTagSelectionRequiredForCheckout(albumFolder) &&
      canSelectActivitities(tags)
    ) {
      resetTagSelection();
      history.replace(`/purchase/album-folder/${albumFolder.id}/activities`);
    } else if (isPrepaidOnlyAlbumFolder(albumFolder)) {
      history.replace(`/purchase/album-folder/${albumFolder.id}/pre-purchase`);
    } else {
      history.replace(
        `/purchase/album-folder/${albumFolder.id}/purchase-summary`,
      );
    }
  };

  const onIdentityFilterChange = filter => {
    setIdentityFilter(filter);
  };

  const hasIdentities = (identities?.length ?? 0) > 0;

  const hasOnlyMeIdentity =
    identities.length === 1 && identities[0]?.name === 'Me';

  // Check if we should show the filter step
  const showFilterStep =
    !loading &&
    albumFolder &&
    !identityFilterStepCompleted &&
    hasOnlyMeIdentity &&
    albumFolder.album_identity_access === ALBUM_IDENTITY_ACCESS.ALL &&
    identities[0].relationship === IDENTITY_RELATIONSHIP.PRIMARY;

  if (loading || !albumFolder) {
    return <Spinner />;
  }

  return (
    <Fragment>
      <WaldoSpeechBubble position="right" className="mb-4">
        Tell me who you want me to look for!
      </WaldoSpeechBubble>

      {showFilterStep && (
        <div className="w-100 mb-4">
          <h4 className="mb-3">I&apos;m looking for...</h4>
          <div className="identity-filter-custom-control">
            <CustomControl
              type="checkbox"
              shape="round"
              id="secondary-filter"
              label={
                <span>
                  My{' '}
                  <ContextualString
                    id="identityReference"
                    type={albumFolder?.album_type}
                    subtype={albumFolder?.album_subtype}
                    formatter={identityRef =>
                      identityRef === 'member' ? 'family member' : identityRef
                    }
                  />
                </span>
              }
              checked={identityFilter === IDENTITY_RELATIONSHIP.SECONDARY}
              onChange={() =>
                onIdentityFilterChange(IDENTITY_RELATIONSHIP.SECONDARY)
              }
            />
          </div>
          <div className="identity-filter-custom-control">
            <CustomControl
              type="checkbox"
              shape="round"
              id="primary-filter"
              label="Myself"
              checked={identityFilter === IDENTITY_RELATIONSHIP.PRIMARY}
              onChange={() =>
                onIdentityFilterChange(IDENTITY_RELATIONSHIP.PRIMARY)
              }
            />
          </div>
          <Button
            btnBlock
            className="mt-2"
            disabled={!identityFilter}
            onClick={() => setIdentityFilterStepCompleted(true)}
          >
            Continue
          </Button>
        </div>
      )}

      {!showFilterStep && (
        <AlbumFolderPurchaseSelectAccountIdentitiesContainer
          allowsPrepaidItems={allowsPrepaidItems}
          identityFilter={identityFilter}
        />
      )}

      {!showFilterStep && hasIdentities && selectedIdentities.length > 0 && (
        <OnboardingLayoutFooter>
          {hasIdentities && (
            <Fragment>
              <Button
                onClick={onGoToCheckout}
                btnBlock
                className="mt-3"
                disabled={selectedIdentities.length === 0}
              >
                Continue
              </Button>
              <Button
                onClick={() => history.goBack()}
                btnBlock
                btnStyle="link"
                className="mt-1 text-center text-bold"
              >
                Go Back
              </Button>
            </Fragment>
          )}
        </OnboardingLayoutFooter>
      )}
    </Fragment>
  );
};

AlbumFolderPurchaseSelectIdentities.propTypes = propTypes;

export default AlbumFolderPurchaseSelectIdentities;
